import React from 'react';
import {
  VMK_PROFILE_NAME,
  getLetterLabel,
  buildSpecRows,
  getItemHeaderSubtitle,
  formatMm,
  formatVmkUnitPrice,
  sqmDisplayFromMmDims,
} from '../lib/vmkQuoteTable';
import VmkQuoteDrawing from './VmkQuoteDrawing';

const VmkDimensionCell = ({ value }) => {
  if (!value) return '\u00a0';
  const mmMatch = String(value).match(/^(\d+)(mm)$/i);
  if (mmMatch) {
    return (
      <>
        <span className="vmk-dim-num">{mmMatch[1]}</span>
        <span className="vmk-dim-unit">mm</span>
      </>
    );
  }
  const sqmMatch = String(value).match(/^([\d.,]+)\s*(m²)$/i);
  if (sqmMatch) {
    return (
      <>
        <span className="vmk-dim-num">{sqmMatch[1]}</span>
        <span className="vmk-dim-unit"> {sqmMatch[2]}</span>
      </>
    );
  }
  return value;
};

const VmkQuoteLineItem = ({
  quote,
  index,
  svgHtml,
  lineTotal,
  formatPrice,
  drawingLoadingLabel,
  onRemove,
  showRemove,
  removeLabel,
  compact = false,
  showPricing = true,
}) => {
  const config = quote.config || {};
  const quantity = config.quantity ?? 1;
  const unitPrice = quantity > 0 ? lineTotal / quantity : lineTotal;
  const letter = getLetterLabel(index);
  const subtitle = getItemHeaderSubtitle(config);
  const specRows = buildSpecRows(config);
  const width = config.width || 0;
  const height = config.height || 0;
  const profileLabel = subtitle ? `${VMK_PROFILE_NAME} ${subtitle.split(' - ')[0]}` : VMK_PROFILE_NAME;

  return (
    <div className="vmk-line-item flex gap-3 print:break-inside-avoid">
      <div className="vmk-drawing flex-shrink-0 w-[36%]">
        {svgHtml ? (
          <VmkQuoteDrawing svgHtml={svgHtml} width={width} height={height} compact={compact} />
        ) : (
          <p className="text-gray-500 text-xs italic pt-4">{drawingLoadingLabel}</p>
        )}
      </div>

      <div className="vmk-spec-table flex-1 min-w-0">
        <table className="vmk-quote-table">
          <colgroup>
            <col className="vmk-c-col-index" />
            <col className="vmk-c-col-label" />
            <col className="vmk-c-col-desc" />
            <col className="vmk-c-col-dim" />
            <col className="vmk-c-col-dim" />
          </colgroup>
          <tbody>
            <tr>
              <td className="vmk-cell-center">{quantity}x</td>
              <td className="vmk-cell-center vmk-cell-letter">{letter}</td>
              <td colSpan={3} className="vmk-cell-subtitle">
                {subtitle || '\u00a0'}
              </td>
            </tr>

            <tr>
              <td className="vmk-cell-center vmk-cell-index" rowSpan={specRows.length + 1}>
                1
              </td>
              <td colSpan={2} className="vmk-cell-desc vmk-cell-profile-row">
                {profileLabel}
              </td>
              <td className="vmk-cell-dim"><VmkDimensionCell value={formatMm(width)} /></td>
              <td className="vmk-cell-dim"><VmkDimensionCell value={formatMm(height)} /></td>
            </tr>

            {specRows.map((row, rowIdx) => {
              const dims = sqmDisplayFromMmDims(row.dim1, row.dim2);
              const showSqm = Boolean(dims.dim1 && dims.dim1.includes('m²'));
              return (
                <tr key={rowIdx}>
                  <td colSpan={2} className="vmk-cell-desc vmk-cell-desc--spec">
                    <div>{row.description}</div>
                    {row.subline ? <div className="vmk-cell-subline">{row.subline}</div> : null}
                  </td>
                  {showSqm ? (
                    <td colSpan={2} className="vmk-cell-dim vmk-cell-dim--sqm">
                      <VmkDimensionCell value={dims.dim1} />
                    </td>
                  ) : (
                    <>
                      <td className="vmk-cell-dim"><VmkDimensionCell value={dims.dim1 || ''} /></td>
                      <td className="vmk-cell-dim"><VmkDimensionCell value={dims.dim2 || ''} /></td>
                    </>
                  )}
                </tr>
              );
            })}

            {showPricing ? (
              <tr className="vmk-price-row vmk-yellow-row">
                <td colSpan={2} className="vmk-cell-price-label">
                  <span className="vmk-price-cell-inner vmk-price-cell-inner--label">Eenheidsprijs :</span>
                </td>
                <td colSpan={3} className="vmk-cell-price-value">
                  <span className="vmk-price-cell-inner vmk-price-cell-inner--value">
                    {formatVmkUnitPrice(unitPrice, quantity, lineTotal, formatPrice)}
                  </span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>

        {showRemove && onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="vmk-remove-btn mt-2 text-red-600 text-xs print:hidden pdf-export-hide hover:underline"
          >
            {removeLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default VmkQuoteLineItem;
